/**
 * SEO.js  –  src/components/SEO.js
 *
 * Drop-in per-page <head> manager using react-helmet-async.
 *
 * Usage (directly in a page component, optional):
 *   import SEO from '../components/SEO';
 *   <SEO title="About Us" description="Learn about AALI GROUP…" canonical="/about" />
 *
 * Or just rely on the central <SEOManager> in App.js, which picks
 * the right config from seoConfig.js for the current route automatically.
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL      = 'https://aaligroup.co';
const DEFAULT_IMAGE = `${BASE_URL}/assets/aali-group-logo.png`;
const SITE_NAME     = 'AALI GROUP';

/**
 * @param {string}           title       – Page title (suffix " | AALI GROUP" added automatically
 *                                         unless the title already contains "AALI GROUP")
 * @param {string}           description – Meta description / OG description
 * @param {string}           [canonical] – Canonical path e.g. "/about"  (domain prepended)
 * @param {string}           [ogImage]   – Absolute URL to OG image (1200×630 recommended)
 * @param {string}           [ogType]    – "website" | "article" | "profile"
 * @param {object|object[]}  [schema]    – JSON-LD object or array of objects
 * @param {boolean}          [noindex]   – If true, adds robots: noindex, nofollow
 */
function SEO({
  title,
  description,
  canonical,
  ogImage   = DEFAULT_IMAGE,
  ogType    = 'website',
  schema    = null,
  noindex   = false,
}) {
  /**
   * Each prerendered HTML file ships static <head> tags (description,
   * canonical, Open Graph, JSON-LD) marked data-seo-fallback, so crawlers and
   * link previews that don't run JavaScript still see the right page data.
   * Once React mounts, Helmet owns the <head> — so the static copies must be
   * removed, or the rendered page would carry two canonicals / descriptions
   * and Google would treat the page as a duplicate. Runs once, on mount.
   */
  useEffect(() => {
    document
      .querySelectorAll('head [data-seo-fallback]')
      .forEach((node) => node.remove());
  }, []);

  // Append brand suffix unless it's already there
  const fullTitle    = title && !title.includes('AALI GROUP')
    ? `${title} | ${SITE_NAME}`
    : (title || `${SITE_NAME} | Diversified Excellence`);

  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : null;

  // Accept both a single schema object and an array
  const schemas = schema
    ? (Array.isArray(schema) ? schema : [schema])
    : [];

  return (
    <Helmet>
      {/* ── Primary ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'}
      />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* ── Local / geo targeting ──
          Bing and several regional engines still read these. Google relies on
          the LocalBusiness JSON-LD instead, but they cost nothing and keep the
          location signal consistent across every crawler. */}
      <meta name="geo.region"    content="IN-AP" />
      <meta name="geo.placename" content="Guntur, Andhra Pradesh" />
      <meta name="geo.position"  content="16.3071219;80.4097439" />
      <meta name="ICBM"          content="16.3071219, 80.4097439" />

      {/* ── Open Graph ── */}
      <meta property="og:type"        content={ogType} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt"   content={fullTitle} />
      {canonicalUrl && <meta property="og:url"       content={canonicalUrl} />}
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:locale"      content="en_IN" />

      {/* ── Twitter Card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />
      <meta name="twitter:image:alt"   content={fullTitle} />

      {/* ── JSON-LD Structured Data ── */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;